/**
 * MarketFit Analysis Context
 * 
 * Provides state management for the MarketFit analysis page including:
 * - Deal state and selection
 * - Solution, Situation, and Actor data
 * - Connection links between elements
 * - Real-time score calculations
 * - Edit mode and drag state
 * 
 * Uses MarketFitService for API calls with workarounds for missing endpoints.
 * @see docs/api/marketfit_api_gap_analysis.md
 */

import React, { 
  createContext, 
  useContext, 
  useState, 
  useCallback, 
  useEffect, 
  useMemo,
  ReactNode 
} from 'react';
import {
  Deal,
  DealOperationalState,
  ProductSolution,
  CustomerSituation,
  CustomerActor,
  ConnectionLinksData,
  ConnectionLink,
  AlignmentScores,
  SolutionInput,
  SolutionBenefit,
  SituationResource,
  SituationNeed,
  MarketFitEditMode,
  MarketFitDragState,
  ConnectionMatchType,
  AlignmentCalculationResult,
  InputResourceMatch,
  BenefitNeedMatch,
} from '../types/marketFit';
import { MarketFitService, UpdateDealRequest } from '../services/api/marketFitService';
import { getMarketFitDealDetail } from '../services/localBridgeSessionApi';
import { mapMarketFitDealDetailToContext } from '../services/marketFitLocalBridgeMapper';
import { isLocalBridgeApiEnabled } from '../services/bridgeSessionDataAdapter';

// ============================================================================
// TYPES
// ============================================================================

export interface MarketFitState {
  // Core data
  deal: Deal | null;
  solution: ProductSolution | null;
  situation: CustomerSituation | null;
  actor: CustomerActor | null;
  connectionLinks: ConnectionLinksData;
  
  // Calculated scores
  scores: AlignmentScores;
  calculationResult: AlignmentCalculationResult | null;
  
  // UI State
  editMode: MarketFitEditMode;
  dragState: MarketFitDragState;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  
  // Dirty state tracking
  hasUnsavedChanges: boolean;
}

export interface MarketFitActions {
  // Data loading
  loadDeal: (dealId: string) => Promise<void>;
  createDeal: (name: string, solutionId: string, situationId: string, actorId: string, companyId?: string) => Promise<void>;
  saveDeal: () => Promise<void>;
  
  // Solution management
  setSolution: (solution: ProductSolution) => void;
  addSolutionInput: (input: Omit<SolutionInput, 'id'>) => void;
  updateSolutionInput: (inputId: string, updates: Partial<SolutionInput>) => void;
  removeSolutionInput: (inputId: string) => void;
  addSolutionBenefit: (benefit: Omit<SolutionBenefit, 'id'>) => void;
  updateSolutionBenefit: (benefitId: string, updates: Partial<SolutionBenefit>) => void;
  removeSolutionBenefit: (benefitId: string) => void;
  
  // Situation management
  setSituation: (situation: CustomerSituation) => void;
  addSituationResource: (resource: Omit<SituationResource, 'id'>) => void;
  updateSituationResource: (resourceId: string, updates: Partial<SituationResource>) => void;
  removeSituationResource: (resourceId: string) => void;
  addSituationNeed: (need: Omit<SituationNeed, 'id'>) => void;
  updateSituationNeed: (needId: string, updates: Partial<SituationNeed>) => void;
  removeSituationNeed: (needId: string) => void;
  
  // Actor management
  setActor: (actor: CustomerActor) => void;
  setActorSituationAlignment: (alignment: number) => void;
  
  // Connection link management
  addInputResourceLink: (inputId: string, resourceId: string, matchType: ConnectionMatchType) => void;
  removeInputResourceLink: (linkId: string) => void;
  updateInputResourceLink: (linkId: string, updates: Partial<ConnectionLink>) => void;
  addBenefitNeedLink: (benefitId: string, needId: string, matchType: ConnectionMatchType) => void;
  removeBenefitNeedLink: (linkId: string) => void;
  updateBenefitNeedLink: (linkId: string, updates: Partial<ConnectionLink>) => void;
  autoDetectLinks: () => void;
  clearAllLinks: () => void;
  
  // Calculation
  recalculateScores: () => void;
  
  // UI state management
  setEditMode: (editMode: Partial<MarketFitEditMode>) => void;
  setDragState: (dragState: Partial<MarketFitDragState>) => void;
  clearError: () => void;
  resetState: () => void;
}

export interface MarketFitContextType extends MarketFitState, MarketFitActions {}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const defaultConnectionLinks: ConnectionLinksData = {
  solutionInputToSituationResourceLinks: [],
  solutionBenefitToSituationNeedLinks: [],
};

const defaultScores: AlignmentScores = {
  productInputAlignment: 0,
  productBenefitAlignment: 0,
  productSituationAlignment: 0,
  productSituationValue: 0,
  actorSituationAlignment: 0,
  dealScore: 0,
};

const defaultEditMode: MarketFitEditMode = {
  isEditing: false,
  editingSection: null,
  editingItemId: null,
};

const defaultDragState: MarketFitDragState = {
  isDragging: false,
  dragType: null,
  dragItemId: null,
};

const defaultState: MarketFitState = {
  deal: null,
  solution: null,
  situation: null,
  actor: null,
  connectionLinks: defaultConnectionLinks,
  scores: defaultScores,
  calculationResult: null,
  editMode: defaultEditMode,
  dragState: defaultDragState,
  isLoading: false,
  isSaving: false,
  error: null,
  hasUnsavedChanges: false,
};

// ============================================================================
// CONTEXT
// ============================================================================

const MarketFitContext = createContext<MarketFitContextType | undefined>(undefined);

// ============================================================================
// CALCULATION UTILITIES
// ============================================================================

const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Calculate similarity between two strings (0-1)
 */
const calculateStringSimilarity = (str1: string, str2: string): number => {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 1.0;
  
  // Check for partial match
  if (s1.includes(s2) || s2.includes(s1)) return 0.7;
  
  // Check for word overlap
  const words1 = s1.split(/\s+/);
  const words2 = s2.split(/\s+/);
  const commonWords = words1.filter(w => words2.includes(w));
  const totalUniqueWords = new Set([...words1, ...words2]).size;
  
  if (totalUniqueWords === 0) return 0;
  return commonWords.length / totalUniqueWords;
};

/**
 * Auto-detect potential links between inputs and resources
 */
const autoDetectInputResourceLinks = (
  inputs: SolutionInput[],
  resources: SituationResource[]
): ConnectionLink[] => {
  const links: ConnectionLink[] = [];
  
  for (const input of inputs) {
    let bestMatch: { resource: SituationResource; similarity: number } | null = null;
    
    for (const resource of resources) {
      const nameSimilarity = calculateStringSimilarity(input.name, resource.name);
      const descSimilarity = input.description && resource.description
        ? calculateStringSimilarity(input.description, resource.description)
        : 0;
      
      const similarity = Math.max(nameSimilarity, descSimilarity * 0.8);
      
      if (similarity > 0.5 && (!bestMatch || similarity > bestMatch.similarity)) {
        bestMatch = { resource, similarity };
      }
    }
    
    if (bestMatch) {
      const matchType: ConnectionMatchType = 
        bestMatch.similarity >= 0.9 ? 'full' : 
        bestMatch.similarity >= 0.5 ? 'partial' : 'none';
      
      links.push({
        id: generateId(),
        fromId: input.id,
        toId: bestMatch.resource.id,
        matchType,
        userOverride: false,
        confidence: bestMatch.similarity,
      });
    }
  }
  
  return links;
};

/**
 * Auto-detect potential links between benefits and needs
 */
const autoDetectBenefitNeedLinks = (
  benefits: SolutionBenefit[],
  needs: SituationNeed[]
): ConnectionLink[] => {
  const links: ConnectionLink[] = [];
  
  for (const benefit of benefits) {
    for (const need of needs) {
      const nameSimilarity = calculateStringSimilarity(benefit.name, need.name);
      const descSimilarity = benefit.description && need.description
        ? calculateStringSimilarity(benefit.description, need.description)
        : 0;
      const valuePropSimilarity = benefit.valueProposition && need.description
        ? calculateStringSimilarity(benefit.valueProposition, need.description)
        : 0;
      
      const similarity = Math.max(nameSimilarity, descSimilarity * 0.8, valuePropSimilarity * 0.9);
      
      if (similarity > 0.4) {
        const matchType: ConnectionMatchType = 
          similarity >= 0.9 ? 'full' : 
          similarity >= 0.5 ? 'partial' : 'none';
        
        links.push({
          id: generateId(),
          fromId: benefit.id,
          toId: need.id,
          matchType,
          userOverride: false,
          confidence: similarity,
        });
      }
    }
  }
  
  return links;
};

/**
 * Calculate alignment scores from connection links and data
 */
const calculateAlignmentScores = (
  solution: ProductSolution | null,
  situation: CustomerSituation | null,
  connectionLinks: ConnectionLinksData,
  actorAlignment: number
): AlignmentCalculationResult => {
  const emptyResult: AlignmentCalculationResult = {
    scores: { ...defaultScores, actorSituationAlignment: actorAlignment },
    inputMatches: [],
    benefitMatches: [],
    unmatchedInputs: [],
    unmatchedNeeds: [],
    newRequiredResources: [],
    totalCost: 0,
  };
  
  if (!solution || !situation) {
    return emptyResult;
  }
  
  const { inputs, benefits, productCostCogs, productCostDevelopment } = solution;
  const { resources, needs, valueGenerated, resourceCost } = situation;
  const { solutionInputToSituationResourceLinks, solutionBenefitToSituationNeedLinks } = connectionLinks;
  
  // Calculate input alignment
  const inputMatches: InputResourceMatch[] = [];
  const unmatchedInputs: string[] = [];
  let newRequiredResourcesCost = 0;
  
  for (const input of inputs) {
    const link = solutionInputToSituationResourceLinks.find(l => l.fromId === input.id);
    
    if (link) {
      const resource = resources.find(r => r.id === link.toId);
      inputMatches.push({
        inputId: input.id,
        resourceId: link.toId,
        matchType: link.matchType,
        resourceCost: resource?.cost ?? 0,
        isNewResource: resource ? !resource.isExisting : true,
      });
      
      if (resource && !resource.isExisting) {
        newRequiredResourcesCost += resource.cost;
      }
    } else {
      unmatchedInputs.push(input.id);
    }
  }
  
  // Product Input Alignment: matched / total (partial = 0.5)
  let inputAlignmentSum = 0;
  for (const match of inputMatches) {
    if (match.matchType === 'full') {
      inputAlignmentSum += 1.0;
    } else if (match.matchType === 'partial') {
      inputAlignmentSum += 0.5;
    }
    // 'none' = 0
  }
  const productInputAlignment = inputs.length > 0 
    ? inputAlignmentSum / inputs.length 
    : 1; // No inputs = 100% alignment
  
  // Calculate benefit alignment
  const benefitMatches: BenefitNeedMatch[] = [];
  const matchedNeedIds = new Set<string>();
  
  for (const benefit of benefits) {
    const links = solutionBenefitToSituationNeedLinks.filter(l => l.fromId === benefit.id);
    const needIds = links.map(l => l.toId);
    
    // Determine overall match type for this benefit
    let matchType: ConnectionMatchType = 'none';
    if (links.some(l => l.matchType === 'full')) {
      matchType = 'full';
    } else if (links.some(l => l.matchType === 'partial')) {
      matchType = 'partial';
    }
    
    benefitMatches.push({
      benefitId: benefit.id,
      needIds,
      matchType,
    });
    
    needIds.forEach(id => matchedNeedIds.add(id));
  }
  
  const unmatchedNeeds = needs
    .filter(n => !matchedNeedIds.has(n.id))
    .map(n => n.id);
  
  // Product Benefit Alignment: needs met / total needs
  const productBenefitAlignment = needs.length > 0 
    ? matchedNeedIds.size / needs.length 
    : 1; // No needs = 100% alignment
  
  // Product Situation Alignment = input alignment × benefit alignment
  const productSituationAlignment = productInputAlignment * productBenefitAlignment;
  
  // Calculate total cost
  const totalCost = resourceCost + productCostCogs + productCostDevelopment + newRequiredResourcesCost;
  
  // Product Situation Value = value generated / total cost
  const productSituationValue = totalCost > 0 ? valueGenerated / totalCost : 0;
  
  // Deal Score = alignment × value × actor alignment
  const dealScore = productSituationAlignment * productSituationValue * actorAlignment;
  
  // New required resources (inputs without existing resources)
  const newRequiredResources: SituationResource[] = unmatchedInputs.map(inputId => {
    const input = inputs.find(i => i.id === inputId);
    return {
      id: generateId(),
      name: input?.name ?? 'Unknown Resource',
      description: `Required for: ${input?.name ?? 'Unknown input'}`,
      cost: 0, // Cost to be determined by user
      isExisting: false,
      linkedInputId: inputId,
    };
  });
  
  return {
    scores: {
      productInputAlignment,
      productBenefitAlignment,
      productSituationAlignment,
      productSituationValue,
      actorSituationAlignment: actorAlignment,
      dealScore,
    },
    inputMatches,
    benefitMatches,
    unmatchedInputs,
    unmatchedNeeds,
    newRequiredResources,
    totalCost,
  };
};

// ============================================================================
// PROVIDER
// ============================================================================

interface MarketFitProviderProps {
  children: ReactNode;
  dealId?: string;
  /** When true, load deal from Local Bridge API instead of main MarketFitService */
  useLocalBridge?: boolean;
}

export const MarketFitProvider: React.FC<MarketFitProviderProps> = ({ 
  children,
  dealId,
  useLocalBridge,
}) => {
  const [state, setState] = useState<MarketFitState>(defaultState);

  // Recalculate scores when relevant data changes
  useEffect(() => {
    const result = calculateAlignmentScores(
      state.solution,
      state.situation,
      state.connectionLinks,
      state.scores.actorSituationAlignment
    );
    
    setState(prev => ({
      ...prev,
      scores: result.scores,
      calculationResult: result,
    }));
  }, [
    state.solution, 
    state.situation, 
    state.connectionLinks,
    state.scores.actorSituationAlignment
  ]);

  // Load deal when dealId changes
  useEffect(() => {
    if (dealId) {
      void loadDeal(dealId);
    }
  }, [dealId]);

  // ============================================================================
  // DATA LOADING ACTIONS
  // ============================================================================

  const loadDeal = useCallback(async (id: string): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const useLocalBridgeSource = useLocalBridge ?? isLocalBridgeApiEnabled();

    try {
      console.log('[MarketFitContext] Loading deal:', id, 'useLocalBridge:', useLocalBridgeSource);

      if (useLocalBridgeSource) {
        const detail = await getMarketFitDealDetail(id);
        if (detail) {
          const mapped = mapMarketFitDealDetailToContext(detail);
          const deal = {
            id: detail.id,
            name: detail.name,
            description: detail.description ?? undefined,
            state: (detail.state?.toUpperCase() ?? 'ACTIVE') as DealOperationalState,
            productSolutionId: mapped.solution?.id ?? '',
            customerSituationId: mapped.situation?.id ?? '',
            customerActorId: mapped.actor?.id ?? '',
            connectionLinks: mapped.connectionLinks,
            scores: {
              productInputAlignment: 1,
              productBenefitAlignment: 1,
              productSituationAlignment: detail.product_situation_alignment ?? 0,
              productSituationValue: detail.product_situation_value ?? 0,
              actorSituationAlignment: mapped.actorSituationAlignment,
              dealScore: (detail.deal_score ?? 0) / 100,
            },
            createdAt: detail.created_at,
            updatedAt: detail.updated_at,
          };
          console.log('[MarketFitContext] Deal loaded from Local Bridge:', deal.name);
          setState(prev => ({
            ...prev,
            deal,
            solution: mapped.solution,
            situation: mapped.situation,
            actor: mapped.actor,
            connectionLinks: mapped.connectionLinks,
            scores: deal.scores,
            isLoading: false,
            hasUnsavedChanges: false,
            error: null,
          }));
        } else {
          console.log('[MarketFitContext] Deal not found in Local Bridge');
          setState(prev => ({ ...prev, isLoading: false, hasUnsavedChanges: false }));
        }
        return;
      }

      // Use MarketFitService to fetch deal with related artifacts
      const result = await MarketFitService.getDeal(id);

      if (result) {
        console.log('[MarketFitContext] Deal loaded successfully:', result.deal.name);
        setState(prev => ({
          ...prev,
          deal: result.deal,
          solution: result.solution,
          situation: result.situation,
          actor: result.actor,
          connectionLinks: result.deal.connectionLinks,
          scores: result.deal.scores,
          isLoading: false,
          hasUnsavedChanges: false,
          error: null,
        }));
      } else {
        console.log('[MarketFitContext] Deal not found, keeping current state');
        setState(prev => ({ ...prev, isLoading: false, hasUnsavedChanges: false }));
      }
    } catch (error) {
      console.error('[MarketFitContext] Error loading deal:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
        hasUnsavedChanges: false,
      }));
    }
  }, [useLocalBridge]);

  const createDeal = useCallback(async (
    name: string, 
    solutionId: string, 
    situationId: string, 
    actorId: string,
    companyId?: string
  ): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      console.log('[MarketFitContext] Creating deal:', { name, solutionId, situationId, actorId });
      
      // Try to create via API
      if (companyId) {
        try {
          const newDeal = await MarketFitService.createDeal({
            name,
            productSolutionId: solutionId,
            customerSituationId: situationId,
            customerActorId: actorId,
            companyId,
          });
          
          console.log('[MarketFitContext] Deal created via API:', newDeal.id);
          
          setState(prev => ({ 
            ...prev, 
            deal: newDeal,
            isLoading: false,
            hasUnsavedChanges: false,
          }));
          return;
        } catch (apiError) {
          console.warn('[MarketFitContext] API create failed, using local state:', apiError);
        }
      }
      
      // Fallback: Create locally (for demo/mock scenarios)
      const newDeal: Deal = {
        id: generateId(),
        name,
        state: 'INITIALIZE',
        productSolutionId: solutionId,
        customerSituationId: situationId,
        customerActorId: actorId,
        connectionLinks: defaultConnectionLinks,
        scores: defaultScores,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      setState(prev => ({ 
        ...prev, 
        deal: newDeal,
        isLoading: false,
        hasUnsavedChanges: false,
      }));
    } catch (error) {
      console.error('[MarketFitContext] Error creating deal:', error);
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to create deal' 
      }));
    }
  }, []);

  const saveDeal = useCallback(async (): Promise<void> => {
    if (!state.deal) return;
    
    setState(prev => ({ ...prev, isSaving: true, error: null }));
    
    try {
      console.log('[MarketFitContext] Saving deal:', state.deal.id);
      
      // Prepare update request
      const updateRequest: UpdateDealRequest = {
        name: state.deal.name,
        description: state.deal.description,
        connectionLinks: state.connectionLinks,
        actorSituationAlignment: state.scores.actorSituationAlignment,
        scores: {
          dealScore: state.scores.dealScore,
          productSituationAlignment: state.scores.productSituationAlignment,
          productSituationValue: state.scores.productSituationValue,
        },
      };
      
      // Try to save via API
      try {
        await MarketFitService.updateDeal(state.deal.id, updateRequest);
        console.log('[MarketFitContext] Deal saved via API');
      } catch (apiError) {
        console.warn('[MarketFitContext] API save failed, changes stored locally:', apiError);
        // Don't throw - allow local-only operation
      }
      
      // Try backend calculation for validation (optional)
      try {
        const backendScore = await MarketFitService.calculateDealScore(
          state.scores.productSituationAlignment,
          state.scores.productSituationValue,
          state.scores.actorSituationAlignment
        );
        console.log('[MarketFitContext] Backend validation - deal score:', backendScore);
      } catch {
        // Backend calculation is optional, don't fail the save
      }
      
      setState(prev => ({ 
        ...prev, 
        isSaving: false,
        hasUnsavedChanges: false,
      }));
    } catch (error) {
      console.error('[MarketFitContext] Error saving deal:', error);
      setState(prev => ({ 
        ...prev, 
        isSaving: false, 
        error: error instanceof Error ? error.message : 'Failed to save deal' 
      }));
    }
  }, [state.deal, state.connectionLinks, state.scores]);

  // ============================================================================
  // SOLUTION MANAGEMENT ACTIONS
  // ============================================================================

  const setSolution = useCallback((solution: ProductSolution) => {
    setState(prev => ({ 
      ...prev, 
      solution,
      hasUnsavedChanges: true,
    }));
  }, []);

  const addSolutionInput = useCallback((input: Omit<SolutionInput, 'id'>) => {
    setState(prev => {
      if (!prev.solution) return prev;
      
      const newInput: SolutionInput = {
        ...input,
        id: generateId(),
      };
      
      return {
        ...prev,
        solution: {
          ...prev.solution,
          inputs: [...prev.solution.inputs, newInput],
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const updateSolutionInput = useCallback((inputId: string, updates: Partial<SolutionInput>) => {
    setState(prev => {
      if (!prev.solution) return prev;
      
      return {
        ...prev,
        solution: {
          ...prev.solution,
          inputs: prev.solution.inputs.map(input =>
            input.id === inputId ? { ...input, ...updates } : input
          ),
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const removeSolutionInput = useCallback((inputId: string) => {
    setState(prev => {
      if (!prev.solution) return prev;
      
      // Also remove any connection links for this input
      const updatedInputLinks = prev.connectionLinks.solutionInputToSituationResourceLinks
        .filter(link => link.fromId !== inputId);
      
      return {
        ...prev,
        solution: {
          ...prev.solution,
          inputs: prev.solution.inputs.filter(input => input.id !== inputId),
        },
        connectionLinks: {
          ...prev.connectionLinks,
          solutionInputToSituationResourceLinks: updatedInputLinks,
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const addSolutionBenefit = useCallback((benefit: Omit<SolutionBenefit, 'id'>) => {
    setState(prev => {
      if (!prev.solution) return prev;
      
      const newBenefit: SolutionBenefit = {
        ...benefit,
        id: generateId(),
      };
      
      return {
        ...prev,
        solution: {
          ...prev.solution,
          benefits: [...prev.solution.benefits, newBenefit],
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const updateSolutionBenefit = useCallback((benefitId: string, updates: Partial<SolutionBenefit>) => {
    setState(prev => {
      if (!prev.solution) return prev;
      
      return {
        ...prev,
        solution: {
          ...prev.solution,
          benefits: prev.solution.benefits.map(benefit =>
            benefit.id === benefitId ? { ...benefit, ...updates } : benefit
          ),
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const removeSolutionBenefit = useCallback((benefitId: string) => {
    setState(prev => {
      if (!prev.solution) return prev;
      
      // Also remove any connection links for this benefit
      const updatedBenefitLinks = prev.connectionLinks.solutionBenefitToSituationNeedLinks
        .filter(link => link.fromId !== benefitId);
      
      return {
        ...prev,
        solution: {
          ...prev.solution,
          benefits: prev.solution.benefits.filter(benefit => benefit.id !== benefitId),
        },
        connectionLinks: {
          ...prev.connectionLinks,
          solutionBenefitToSituationNeedLinks: updatedBenefitLinks,
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  // ============================================================================
  // SITUATION MANAGEMENT ACTIONS
  // ============================================================================

  const setSituation = useCallback((situation: CustomerSituation) => {
    setState(prev => ({ 
      ...prev, 
      situation,
      hasUnsavedChanges: true,
    }));
  }, []);

  const addSituationResource = useCallback((resource: Omit<SituationResource, 'id'>) => {
    setState(prev => {
      if (!prev.situation) return prev;
      
      const newResource: SituationResource = {
        ...resource,
        id: generateId(),
      };
      
      return {
        ...prev,
        situation: {
          ...prev.situation,
          resources: [...prev.situation.resources, newResource],
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const updateSituationResource = useCallback((resourceId: string, updates: Partial<SituationResource>) => {
    setState(prev => {
      if (!prev.situation) return prev;
      
      return {
        ...prev,
        situation: {
          ...prev.situation,
          resources: prev.situation.resources.map(resource =>
            resource.id === resourceId ? { ...resource, ...updates } : resource
          ),
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const removeSituationResource = useCallback((resourceId: string) => {
    setState(prev => {
      if (!prev.situation) return prev;
      
      // Also remove any connection links for this resource
      const updatedInputLinks = prev.connectionLinks.solutionInputToSituationResourceLinks
        .filter(link => link.toId !== resourceId);
      
      return {
        ...prev,
        situation: {
          ...prev.situation,
          resources: prev.situation.resources.filter(resource => resource.id !== resourceId),
        },
        connectionLinks: {
          ...prev.connectionLinks,
          solutionInputToSituationResourceLinks: updatedInputLinks,
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const addSituationNeed = useCallback((need: Omit<SituationNeed, 'id'>) => {
    setState(prev => {
      if (!prev.situation) return prev;
      
      const newNeed: SituationNeed = {
        ...need,
        id: generateId(),
      };
      
      return {
        ...prev,
        situation: {
          ...prev.situation,
          needs: [...prev.situation.needs, newNeed],
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const updateSituationNeed = useCallback((needId: string, updates: Partial<SituationNeed>) => {
    setState(prev => {
      if (!prev.situation) return prev;
      
      return {
        ...prev,
        situation: {
          ...prev.situation,
          needs: prev.situation.needs.map(need =>
            need.id === needId ? { ...need, ...updates } : need
          ),
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const removeSituationNeed = useCallback((needId: string) => {
    setState(prev => {
      if (!prev.situation) return prev;
      
      // Also remove any connection links for this need
      const updatedBenefitLinks = prev.connectionLinks.solutionBenefitToSituationNeedLinks
        .filter(link => link.toId !== needId);
      
      return {
        ...prev,
        situation: {
          ...prev.situation,
          needs: prev.situation.needs.filter(need => need.id !== needId),
        },
        connectionLinks: {
          ...prev.connectionLinks,
          solutionBenefitToSituationNeedLinks: updatedBenefitLinks,
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  // ============================================================================
  // ACTOR MANAGEMENT ACTIONS
  // ============================================================================

  const setActor = useCallback((actor: CustomerActor) => {
    setState(prev => ({ 
      ...prev, 
      actor,
      hasUnsavedChanges: true,
    }));
  }, []);

  const setActorSituationAlignment = useCallback((alignment: number) => {
    setState(prev => ({
      ...prev,
      scores: {
        ...prev.scores,
        actorSituationAlignment: Math.max(0, Math.min(1, alignment)),
      },
      hasUnsavedChanges: true,
    }));
  }, []);

  // ============================================================================
  // CONNECTION LINK MANAGEMENT ACTIONS
  // ============================================================================

  const addInputResourceLink = useCallback((
    inputId: string, 
    resourceId: string, 
    matchType: ConnectionMatchType
  ) => {
    setState(prev => {
      // Check if link already exists
      const existingLink = prev.connectionLinks.solutionInputToSituationResourceLinks
        .find(link => link.fromId === inputId && link.toId === resourceId);
      
      if (existingLink) return prev;
      
      const newLink: ConnectionLink = {
        id: generateId(),
        fromId: inputId,
        toId: resourceId,
        matchType,
        userOverride: true,
      };
      
      return {
        ...prev,
        connectionLinks: {
          ...prev.connectionLinks,
          solutionInputToSituationResourceLinks: [
            ...prev.connectionLinks.solutionInputToSituationResourceLinks,
            newLink,
          ],
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const removeInputResourceLink = useCallback((linkId: string) => {
    setState(prev => ({
      ...prev,
      connectionLinks: {
        ...prev.connectionLinks,
        solutionInputToSituationResourceLinks: 
          prev.connectionLinks.solutionInputToSituationResourceLinks.filter(
            link => link.id !== linkId
          ),
      },
      hasUnsavedChanges: true,
    }));
  }, []);

  const updateInputResourceLink = useCallback((linkId: string, updates: Partial<ConnectionLink>) => {
    setState(prev => ({
      ...prev,
      connectionLinks: {
        ...prev.connectionLinks,
        solutionInputToSituationResourceLinks: 
          prev.connectionLinks.solutionInputToSituationResourceLinks.map(link =>
            link.id === linkId ? { ...link, ...updates, userOverride: true } : link
          ),
      },
      hasUnsavedChanges: true,
    }));
  }, []);

  const addBenefitNeedLink = useCallback((
    benefitId: string, 
    needId: string, 
    matchType: ConnectionMatchType
  ) => {
    setState(prev => {
      // Check if link already exists
      const existingLink = prev.connectionLinks.solutionBenefitToSituationNeedLinks
        .find(link => link.fromId === benefitId && link.toId === needId);
      
      if (existingLink) return prev;
      
      const newLink: ConnectionLink = {
        id: generateId(),
        fromId: benefitId,
        toId: needId,
        matchType,
        userOverride: true,
      };
      
      return {
        ...prev,
        connectionLinks: {
          ...prev.connectionLinks,
          solutionBenefitToSituationNeedLinks: [
            ...prev.connectionLinks.solutionBenefitToSituationNeedLinks,
            newLink,
          ],
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const removeBenefitNeedLink = useCallback((linkId: string) => {
    setState(prev => ({
      ...prev,
      connectionLinks: {
        ...prev.connectionLinks,
        solutionBenefitToSituationNeedLinks: 
          prev.connectionLinks.solutionBenefitToSituationNeedLinks.filter(
            link => link.id !== linkId
          ),
      },
      hasUnsavedChanges: true,
    }));
  }, []);

  const updateBenefitNeedLink = useCallback((linkId: string, updates: Partial<ConnectionLink>) => {
    setState(prev => ({
      ...prev,
      connectionLinks: {
        ...prev.connectionLinks,
        solutionBenefitToSituationNeedLinks: 
          prev.connectionLinks.solutionBenefitToSituationNeedLinks.map(link =>
            link.id === linkId ? { ...link, ...updates, userOverride: true } : link
          ),
      },
      hasUnsavedChanges: true,
    }));
  }, []);

  const autoDetectLinks = useCallback(() => {
    setState(prev => {
      if (!prev.solution || !prev.situation) return prev;
      
      // Auto-detect input -> resource links
      const inputLinks = autoDetectInputResourceLinks(
        prev.solution.inputs,
        prev.situation.resources
      );
      
      // Auto-detect benefit -> need links
      const benefitLinks = autoDetectBenefitNeedLinks(
        prev.solution.benefits,
        prev.situation.needs
      );
      
      return {
        ...prev,
        connectionLinks: {
          solutionInputToSituationResourceLinks: inputLinks,
          solutionBenefitToSituationNeedLinks: benefitLinks,
        },
        hasUnsavedChanges: true,
      };
    });
  }, []);

  const clearAllLinks = useCallback(() => {
    setState(prev => ({
      ...prev,
      connectionLinks: defaultConnectionLinks,
      hasUnsavedChanges: true,
    }));
  }, []);

  // ============================================================================
  // CALCULATION ACTIONS
  // ============================================================================

  const recalculateScores = useCallback(() => {
    // Client-side calculation (always runs for real-time updates)
    const result = calculateAlignmentScores(
      state.solution,
      state.situation,
      state.connectionLinks,
      state.scores.actorSituationAlignment
    );
    
    setState(prev => ({
      ...prev,
      scores: result.scores,
      calculationResult: result,
    }));
  }, [state.solution, state.situation, state.connectionLinks, state.scores.actorSituationAlignment]);

  /**
   * Validate scores against backend calculation (optional)
   * Uses backend API when available, falls back silently if not
   */
  const validateScoresWithBackend = useCallback(async (): Promise<void> => {
    if (!state.solution || !state.situation) return;

    try {
      // Try backend alignment calculation
      const backendResult = await MarketFitService.calculateProductSituationAlignment(
        state.solution.inputs,
        state.solution.benefits,
        state.situation.resources,
        state.situation.needs
      );

      console.log('[MarketFitContext] Backend alignment validation:', {
        backend: backendResult.alignment,
        frontend: state.scores.productSituationAlignment,
        difference: Math.abs(backendResult.alignment - state.scores.productSituationAlignment),
      });

      // Try backend deal score calculation
      const backendDealScore = await MarketFitService.calculateDealScore(
        state.scores.productSituationAlignment,
        state.scores.productSituationValue,
        state.scores.actorSituationAlignment
      );

      console.log('[MarketFitContext] Backend deal score validation:', {
        backend: backendDealScore,
        frontend: state.scores.dealScore,
        difference: Math.abs(backendDealScore - state.scores.dealScore),
      });
    } catch (error) {
      // Backend validation is optional - don't fail if unavailable
      console.log('[MarketFitContext] Backend validation skipped (API unavailable)');
    }
  }, [state.solution, state.situation, state.scores]);

  // ============================================================================
  // UI STATE ACTIONS
  // ============================================================================

  const setEditMode = useCallback((editMode: Partial<MarketFitEditMode>) => {
    setState(prev => ({
      ...prev,
      editMode: { ...prev.editMode, ...editMode },
    }));
  }, []);

  const setDragState = useCallback((dragState: Partial<MarketFitDragState>) => {
    setState(prev => ({
      ...prev,
      dragState: { ...prev.dragState, ...dragState },
    }));
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const resetState = useCallback(() => {
    setState(defaultState);
  }, []);

  // ============================================================================
  // MEMOIZED CONTEXT VALUE
  // ============================================================================

  const contextValue = useMemo<MarketFitContextType>(() => ({
    // State
    ...state,
    
    // Actions
    loadDeal,
    createDeal,
    saveDeal,
    setSolution,
    addSolutionInput,
    updateSolutionInput,
    removeSolutionInput,
    addSolutionBenefit,
    updateSolutionBenefit,
    removeSolutionBenefit,
    setSituation,
    addSituationResource,
    updateSituationResource,
    removeSituationResource,
    addSituationNeed,
    updateSituationNeed,
    removeSituationNeed,
    setActor,
    setActorSituationAlignment,
    addInputResourceLink,
    removeInputResourceLink,
    updateInputResourceLink,
    addBenefitNeedLink,
    removeBenefitNeedLink,
    updateBenefitNeedLink,
    autoDetectLinks,
    clearAllLinks,
    recalculateScores,
    setEditMode,
    setDragState,
    clearError,
    resetState,
  }), [
    state,
    loadDeal,
    createDeal,
    saveDeal,
    setSolution,
    addSolutionInput,
    updateSolutionInput,
    removeSolutionInput,
    addSolutionBenefit,
    updateSolutionBenefit,
    removeSolutionBenefit,
    setSituation,
    addSituationResource,
    updateSituationResource,
    removeSituationResource,
    addSituationNeed,
    updateSituationNeed,
    removeSituationNeed,
    setActor,
    setActorSituationAlignment,
    addInputResourceLink,
    removeInputResourceLink,
    updateInputResourceLink,
    addBenefitNeedLink,
    removeBenefitNeedLink,
    updateBenefitNeedLink,
    autoDetectLinks,
    clearAllLinks,
    recalculateScores,
    setEditMode,
    setDragState,
    clearError,
    resetState,
  ]);

  return (
    <MarketFitContext.Provider value={contextValue}>
      {children}
    </MarketFitContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export const useMarketFit = (): MarketFitContextType => {
  const context = useContext(MarketFitContext);
  
  if (!context) {
    throw new Error('useMarketFit must be used within a MarketFitProvider');
  }
  
  return context;
};

export default MarketFitContext;
